import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, X, Info, ListChecks, Upload, Download, Search, Pin, PinOff,
  Archive, ArchiveRestore, Copy, Trash2, Pencil, MoreHorizontal, ChevronDown,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, downloadCsvViaApi, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import ImportWizard from "../../components/app/ImportWizard";
import { Checkbox } from "../../components/ui/checkbox";
import { BulkActionsBar } from "../../components/app/BulkActionsBar";

const PALETTE = ["#22C55E", "#143A25", "#0E9F6E", "#4ADE80", "#F97316", "#0EA5E9", "#A855F7", "#F43F5E"];

const BILLING_TYPES = [
  { id: "time_materials", label: "Time & Materials" },
  { id: "fixed_fee",      label: "Fixed Fee" },
  { id: "retainer",       label: "Retainer" },
  { id: "non_billable",   label: "Non-Billable" },
];
const BILLING_LABEL = Object.fromEntries(BILLING_TYPES.map((b) => [b.id, b.label]));

export default function Projects() {
  const nav = useNavigate();
  const { activeWorkspace } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [status, setStatus] = useState("active"); // active | archived | all | pinned
  const [clientId, setClientId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [q, setQ] = useState("");
  const [openProj, setOpenProj] = useState(null); // null | "new" | project obj (edit)
  const [openClient, setOpenClient] = useState(false);
  const [importFor, setImportFor] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    const params = { status };
    if (clientId) params.client_id = clientId;
    if (managerId) params.manager_id = managerId;
    if (q.trim()) params.q = q.trim();
    const [p, c, rc, m] = await Promise.all([
      api.projects(params),
      api.clients(),
      api.rateCards(),
      activeWorkspace?.id ? api.members(activeWorkspace.id).catch(() => []) : Promise.resolve([]),
    ]);
    setProjects(p); setClients(c); setRateCards(rc); setMembers(m);
    setSelected(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, clientId, managerId]);
  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const grouped = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      const c = clientById[p.client_id];
      const key = c?.name || "Unassigned";
      (map[key] = map[key] || { name: key, client: c, rows: [] }).rows.push(p);
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, clientById]);

  const count = projects.length;

  const doAction = async (p, action) => {
    try {
      if (action === "pin") await api.pinProject(p.id);
      if (action === "archive") await api.archiveProject(p.id);
      if (action === "unarchive") await api.unarchiveProject(p.id);
      if (action === "duplicate") {
        const cp = await api.duplicateProject(p.id);
        toast({ title: "Project duplicated", description: cp.name });
      }
      if (action === "delete") {
        if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
        await api.deleteProject(p.id);
        toast({ title: "Project deleted" });
      }
      await load();
    } catch (ex) {
      toast({ title: "Couldn't complete action", description: apiErrorMessage(ex, "") });
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const toggleSelectAll = () => setSelected((prev) =>
    prev.size === projects.length ? new Set() : new Set(projects.map((p) => p.id))
  );

  const bulkRun = async (fn, ids, label) => {
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) toast({ title: `${ok} project(s) ${label}` });
    if (failed > 0) toast({ title: `${failed} project(s) failed`, description: "Some items may need to be retried individually." });
    clearSelection();
    await load();
  };
  const bulkArchive = () => bulkRun(api.archiveProject, [...selected], "archived");
  const bulkUnarchive = () => bulkRun(api.unarchiveProject, [...selected], "unarchived");
  const bulkDelete = () => {
    const ids = [...selected];
    if (!window.confirm(`Delete ${ids.length} selected project${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    bulkRun(api.deleteProject, ids, "deleted");
  };

  return (
    <div data-testid="projects-page">
      {/* Header row */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f]">Projects</div>
          <h1 className="font-display text-4xl mt-1">Projects</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setOpenProj("new")}
                  data-testid="new-project-btn"
                  className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
            <Plus size={16} /> New project
          </button>
          <button onClick={() => setOpenClient(true)}
                  data-testid="new-client-btn"
                  className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
            <Plus size={14} /> New client
          </button>
          <button onClick={() => setImportFor("projects")}
                  data-testid="import-projects-btn"
                  className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
            <Upload size={14} /> Import
          </button>
          <button
            onClick={() => downloadCsvViaApi("/projects/export.csv", { status }, `projects_${status}.csv`)}
            data-testid="export-projects-btn"
            className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
            <Download size={14} /> Export
          </button>
          <BulkActionsBar
            count={selected.size}
            onClear={clearSelection}
            testidPrefix="projects"
            actions={[
              { key: "archive", label: `Archive selected${selected.size ? ` (${selected.size})` : ""}`, icon: Archive, onClick: bulkArchive },
              { key: "unarchive", label: `Unarchive selected${selected.size ? ` (${selected.size})` : ""}`, icon: ArchiveRestore, onClick: bulkUnarchive },
              { key: "delete", label: `Delete selected${selected.size ? ` (${selected.size})` : ""}`, icon: Trash2, danger: true, onClick: bulkDelete },
            ]}
          />
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-[#6a736f]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by project or client…"
              data-testid="project-search"
              className="pl-8 pr-3 py-2 rounded-md border border-cream text-sm w-64"
            />
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <StatusPicker status={status} setStatus={setStatus} count={count} />
        <div className="flex items-center gap-2">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                  data-testid="filter-client"
                  className="border border-cream rounded-md px-3 py-2 text-sm bg-white">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)}
                  data-testid="filter-manager"
                  className="border border-cream rounded-md px-3 py-2 text-sm bg-white">
            <option value="">All managers</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      {rateCards.length === 0 && status === "active" && projects.length === 0 && (
        <div className="mt-6 bg-cream-2 border border-cream rounded-lg px-4 py-3 text-sm text-forest flex items-start gap-2">
          <Info size={16} className="text-coral mt-0.5" />
          <div>
            No rate cards yet. <Link to="/app/rate-cards" className="text-coral font-medium">Create one</Link> to save standard rates and pull them into new projects.
          </div>
        </div>
      )}

      {/* Grouped table */}
      <div className="mt-4 bg-white rounded-xl border border-cream overflow-hidden">
        <div className="grid grid-cols-[28px_minmax(240px,1fr)_140px_180px_180px_140px_60px] gap-3 items-center px-4 py-2 bg-cream-2/50 text-xs text-[#6a736f]">
          <Checkbox checked={projects.length > 0 && selected.size === projects.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-projects" />
          <div>Client · Project</div>
          <div className="text-right">Budget</div>
          <div>Spent</div>
          <div className="text-right">Remaining</div>
          <div className="text-right">Costs</div>
          <div></div>
        </div>
        {projects.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6a736f]" data-testid="projects-empty">
            No {status === "active" ? "active" : status} projects.{status === "archived" && " Archive one from the list to see it here."}
          </div>
        ) : grouped.map((g) => (
          <div key={g.name} data-testid={`client-group-${g.name}`}>
            <div className="px-4 py-2 bg-cream-2/20 text-sm">
              <span className="inline-flex items-center gap-2 font-semibold text-forest">
                <span className="h-2 w-2 rounded-full" style={{ background: g.client?.color || "#22C55E" }} />
                {g.name}
              </span>
              <span className="text-xs text-[#6a736f] ml-2">({g.rows.length})</span>
            </div>
            {g.rows.map((p) => (
              <ProjectRow
                key={p.id} p={p}
                selected={selected.has(p.id)}
                onToggleSelect={() => toggleSelect(p.id)}
                onOpen={() => nav(`/app/projects/${p.id}`)}
                onEdit={() => setOpenProj(p)}
                onAction={(action) => doAction(p, action)}
              />
            ))}
          </div>
        ))}
      </div>

      {openProj && (
        <ProjectFormModal
          initial={openProj === "new" ? null : openProj}
          clients={clients}
          rateCards={rateCards}
          onClose={() => setOpenProj(null)}
          onSaved={async () => { setOpenProj(null); await load(); }}
          toast={toast}
        />
      )}

      {openClient && (
        <ClientFormModal
          onClose={() => setOpenClient(false)}
          onSaved={async (c) => {
            setOpenClient(false);
            setClients((prev) => [c, ...prev]);
            toast({ title: "Client added", description: c.name });
          }}
        />
      )}

      <ImportWizard
        entity={importFor}
        open={!!importFor}
        onClose={() => setImportFor(null)}
        onImported={() => load()}
        toast={toast}
      />
    </div>
  );
}

// ---------------------------------------------------------------- Status picker
function StatusPicker({ status, setStatus, count }) {
  const options = [
    { id: "active",   label: "Active projects" },
    { id: "pinned",   label: "Pinned" },
    { id: "archived", label: "Archived" },
    { id: "all",      label: "All projects" },
  ];
  const [open, setOpen] = useState(false);
  const label = options.find((o) => o.id === status)?.label || "Active projects";
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
              data-testid="status-picker"
              className="border border-cream rounded-md px-4 py-2 text-sm bg-white inline-flex items-center gap-2">
        <span className="font-semibold">{label}</span>
        <span className="text-[#6a736f]">({count})</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute mt-1 z-10 bg-white border border-cream rounded-md shadow-md w-56 py-1"
             onMouseLeave={() => setOpen(false)}>
          {options.map((o) => (
            <button key={o.id}
                    onClick={() => { setStatus(o.id); setOpen(false); }}
                    data-testid={`status-option-${o.id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-cream-2/60 ${
                      status === o.id ? "text-coral font-semibold" : "text-forest"
                    }`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Row
function ProjectRow({ p, selected, onToggleSelect, onOpen, onEdit, onAction }) {
  const budgetTotal = (p.rate || 0) * (p.budget_hours || 0);
  // Revenue = rate × spent hours (fallback for now — real revenue lives in detail)
  const revenue = (p.rate || 0) * (p.spent_hours || 0);
  const remaining = budgetTotal - revenue;
  const spentPct = budgetTotal > 0 ? Math.min(200, (revenue / budgetTotal) * 100) : (p.budget_hours > 0 ? Math.min(200, (p.spent_hours / p.budget_hours) * 100) : 0);
  const over = spentPct > 100;
  const costs = 0; // filled from server (we don't have it in list — leave "—")
  const remainingPct = budgetTotal > 0 ? Math.round(((budgetTotal - revenue) / budgetTotal) * 100) : null;
  return (
    <div className="grid grid-cols-[28px_minmax(240px,1fr)_140px_180px_180px_140px_60px] gap-3 items-center px-4 py-3 border-t border-cream/60 hover:bg-cream-2/30"
         data-testid={`project-row-${p.id}`}>
      <Checkbox checked={selected} onCheckedChange={onToggleSelect} data-testid={`select-project-${p.id}`} />
      <button onClick={onOpen} className="text-left min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {p.is_pinned && <Pin size={12} className="text-coral shrink-0" />}
          <span className="font-medium truncate">
            {p.code && <span className="text-[#6a736f] mr-1">[{p.code}]</span>}
            {p.name}
          </span>
          <BillingPill type={p.billing_type} />
        </div>
      </button>
      <div className="text-right tabular-nums text-sm">
        {p.billing_type === "non_billable" ? "—" : budgetTotal ? fmt(budgetTotal) : `${p.budget_hours || 0}h`}
      </div>
      <div>
        {p.billing_type === "non_billable" ? (
          <div className="text-sm tabular-nums">{Number(p.spent_hours || 0).toFixed(2)}h</div>
        ) : (
          <div>
            <div className="h-2 bg-[#EAF3EE] rounded-full overflow-hidden relative">
              {over ? (
                <>
                  <div className="absolute left-0 top-0 h-full bg-blue-500" style={{ width: "50%" }} />
                  <div className="absolute right-0 top-0 h-full bg-red-500" style={{ width: `${Math.min(50, spentPct - 100)}%` }} />
                </>
              ) : (
                <div className="h-full bg-blue-500" style={{ width: `${spentPct}%` }} />
              )}
            </div>
            <div className="text-[10px] text-[#6a736f] mt-0.5 tabular-nums">{fmt(revenue)}</div>
          </div>
        )}
      </div>
      <div className="text-right tabular-nums text-sm">
        {p.billing_type === "non_billable" ? "—" : (
          <span className={remaining < 0 ? "text-red-600 font-semibold" : ""}>
            {fmt(remaining)}{remainingPct !== null && (
              <span className="text-[10px] text-[#6a736f] ml-1">({remainingPct}%)</span>
            )}
          </span>
        )}
      </div>
      <div className="text-right tabular-nums text-sm text-[#6a736f]">
        {/* Costs live in detail view; show em-dash on list for now */}
        —
      </div>
      <RowActions p={p} onEdit={onEdit} onAction={onAction} />
    </div>
  );
}

function BillingPill({ type }) {
  if (!type) return null;
  const label = BILLING_LABEL[type] || type;
  const cls = {
    time_materials: "bg-blue-50 text-blue-700 border-blue-200",
    fixed_fee:      "bg-purple-50 text-purple-700 border-purple-200",
    retainer:       "bg-amber-50 text-amber-700 border-amber-200",
    non_billable:   "bg-cream-2 text-[#6a736f] border-cream",
  }[type] || "bg-cream-2 text-[#6a736f] border-cream";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {label}
    </span>
  );
}

function RowActions({ p, onEdit, onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative flex justify-end" ref={ref}>
      <button onClick={() => setOpen((o) => !o)}
              data-testid={`row-actions-${p.id}`}
              className="p-1.5 rounded hover:bg-cream-2/60 text-[#6a736f]">
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 w-40 bg-white border border-cream rounded-md shadow-md py-1 text-sm">
          <MenuItem onClick={() => { setOpen(false); onEdit(); }} icon={Pencil} testid={`action-edit-${p.id}`}>Edit</MenuItem>
          <MenuItem onClick={() => { setOpen(false); onAction("pin"); }}
                    icon={p.is_pinned ? PinOff : Pin}
                    testid={`action-pin-${p.id}`}>
            {p.is_pinned ? "Unpin" : "Pin"}
          </MenuItem>
          <MenuItem onClick={() => { setOpen(false); onAction("duplicate"); }} icon={Copy} testid={`action-dup-${p.id}`}>Duplicate</MenuItem>
          <div className="my-1 border-t border-cream" />
          {p.is_archived ? (
            <MenuItem onClick={() => { setOpen(false); onAction("unarchive"); }} icon={ArchiveRestore} testid={`action-unarchive-${p.id}`}>Unarchive</MenuItem>
          ) : (
            <MenuItem onClick={() => { setOpen(false); onAction("archive"); }} icon={Archive} testid={`action-archive-${p.id}`}>Archive</MenuItem>
          )}
          <MenuItem onClick={() => { setOpen(false); onAction("delete"); }} icon={Trash2} danger testid={`action-delete-${p.id}`}>Delete</MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, icon: Icon, children, danger, testid }) {
  return (
    <button onClick={onClick}
            data-testid={testid}
            className={`w-full text-left px-3 py-1.5 hover:bg-cream-2/60 inline-flex items-center gap-2 ${danger ? "text-red-600" : "text-forest"}`}>
      <Icon size={12} /> {children}
    </button>
  );
}

function fmt(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------- Modals
function ProjectFormModal({ initial, clients, rateCards, onClose, onSaved, toast }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    client_id: initial?.client_id || clients[0]?.id || "",
    code: initial?.code || "",
    billing_type: initial?.billing_type || "time_materials",
    budget_hours: initial?.budget_hours ?? 40,
    rate: initial?.rate ?? 0,
    rate_card_id: initial?.rate_card_id || "",
    rate_tier: initial?.rate_tier || "",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
  }));
  const [rateMode, setRateMode] = useState(initial?.rate_card_id ? "card" : (rateCards.length ? "card" : "custom"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    if (isEdit || form.code) return;
    api.nextProjectCode().then((r) => setSuggested(r.code)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (rateMode !== "card" || !rateCards.length) return;
    if (!form.rate_card_id) {
      const def = rateCards.find((rc) => rc.is_default) || rateCards[0];
      setForm((f) => ({ ...f, rate_card_id: def.id, rate_tier: def.tiers[0]?.label || "", rate: def.tiers[0]?.rate || 0 }));
    }
    // eslint-disable-next-line
  }, [rateMode]);

  const selectedCard = rateCards.find((rc) => rc.id === form.rate_card_id);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const billable = form.billing_type !== "non_billable";
      const payload = {
        name: form.name,
        client_id: form.client_id,
        code: form.code || null,
        billing_type: form.billing_type,
        billable,
        budget_hours: Number(form.budget_hours) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        ...(rateMode === "card"
          ? { rate_card_id: form.rate_card_id, rate_tier: form.rate_tier, rate: 0 }
          : { rate: Number(form.rate) || 0, rate_card_id: null, rate_tier: null }),
      };
      if (isEdit) {
        await api.updateProject(initial.id, payload);
        toast?.({ title: "Project updated" });
      } else {
        await api.createProject(payload);
        toast?.({ title: "Project created" });
      }
      await onSaved?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save project."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="project-form-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Edit project" : "New project"}</h2>
          <button type="button" onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Project name</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="pf-name"
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Code</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                   placeholder={suggested || "P-0001"}
                   data-testid="pf-code"
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm font-mono" />
            {!isEdit && suggested && !form.code && (
              <div className="text-[10px] text-[#6a736f] mt-0.5">Suggested: {suggested}</div>
            )}
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Client</span>
          <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  required data-testid="pf-client"
                  className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
            {clients.length === 0 && <option value="">(no clients yet)</option>}
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Billing type</span>
          <select value={form.billing_type} onChange={(e) => setForm({ ...form, billing_type: e.target.value })}
                  data-testid="pf-billing-type"
                  className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
            {BILLING_TYPES.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </label>

        {form.billing_type !== "non_billable" && (
          <div className="border border-cream rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-3 text-xs">
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={rateMode === "card"} onChange={() => setRateMode("card")} disabled={!rateCards.length} />
                Rate card
              </label>
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={rateMode === "custom"} onChange={() => setRateMode("custom")} />
                Custom rate
              </label>
            </div>
            {rateMode === "card" && rateCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                <select value={form.rate_card_id}
                        onChange={(e) => {
                          const card = rateCards.find((rc) => rc.id === e.target.value);
                          setForm({ ...form, rate_card_id: e.target.value, rate_tier: card?.tiers[0]?.label || "", rate: card?.tiers[0]?.rate || 0 });
                        }}
                        className="border border-cream rounded-md px-3 py-2 text-sm bg-white">
                  {rateCards.map((rc) => <option key={rc.id} value={rc.id}>{rc.name}</option>)}
                </select>
                <select value={form.rate_tier}
                        onChange={(e) => {
                          const tier = selectedCard?.tiers.find((t) => t.label === e.target.value);
                          setForm({ ...form, rate_tier: e.target.value, rate: tier?.rate || 0 });
                        }}
                        className="border border-cream rounded-md px-3 py-2 text-sm bg-white">
                  {selectedCard?.tiers.map((t) => <option key={t.label} value={t.label}>{t.label} · ${t.rate}/hr</option>)}
                </select>
              </div>
            ) : (
              <label className="block text-sm">
                <span className="text-xs font-semibold text-[#3f4a44]">Hourly rate ($)</span>
                <input type="number" min={0} step="0.01"
                       value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })}
                       className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
              </label>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Budget (hours)</span>
            <input type="number" min={0} step="0.5" value={form.budget_hours}
                   onChange={(e) => setForm({ ...form, budget_hours: e.target.value })}
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Start date</span>
            <input type="date" value={form.start_date}
                   onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">End date</span>
            <input type="date" value={form.end_date}
                   onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
        </div>

        {err && <div className="text-sm text-red-600" data-testid="pf-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-cream">
          <button type="button" onClick={onClose} className="btn-outline-forest px-3 py-2 rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid="pf-save"
                  className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
            {busy ? "Saving…" : (isEdit ? "Update project" : "Create project")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ClientFormModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", color: PALETTE[0] });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const c = await api.createClient({ name: form.name, color: form.color });
      await onSaved?.(c);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-md p-6 space-y-4" data-testid="client-form-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">New client</h2>
          <button type="button" onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>
        <label className="block text-sm">
          <span className="font-medium">Name</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
        </label>
        <div>
          <div className="text-sm font-medium mb-2">Colour</div>
          <div className="flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button type="button" key={c} onClick={() => setForm({ ...form, color: c })}
                      className={`h-8 w-8 rounded-full border-2 ${form.color === c ? "border-forest" : "border-transparent"}`}
                      style={{ background: c }} />
            ))}
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-coral w-full py-2.5 rounded-md text-sm font-medium">
          {busy ? "Adding…" : "Add client"}
        </button>
      </form>
    </div>
  );
}
